<template>
  <div class="pro-table">
    <!-- 搜索栏 -->
    <div v-if="showSearch" class="search-form">
      <el-form ref="searchFormRef" :model="searchForm" :inline="true" class="search-form-content">
        <template v-for="column in searchColumns">
          <el-form-item :label="column.label" :prop="column.prop" :key="column.prop">
            <!-- 输入框 -->
            <el-input
              v-if="!column.searchType || column.searchType === 'input'"
              v-model="searchForm[column.prop]"
              :placeholder="column.search?.props?.placeholder || `请输入${column.label}`"
              clearable
              style="width: 200px"
            />
            <!-- 选择器 -->
            <el-select
              v-else-if="column.searchType === 'select'"
              v-model="searchForm[column.prop]"
              :placeholder="column.search?.props?.placeholder || `请选择${column.label}`"
              clearable
              style="width: 200px"
            >
              <el-option
                v-for="option in column.searchOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
            <el-select
              v-else-if="column.searchType === 'select-group'"
              v-model="searchForm[column.prop]"
              :placeholder="column.search?.props?.placeholder || `请选择${column.label}`"
            >
              <el-option-group
                v-for="parent in column.searchOptions"
                :key="parent.id"
                :label="parent.label"
              >
                <el-option
                  v-for="child in parent.children"
                  :key="child.id"
                  :label="child.label"
                  :value="child.id"
                />
              </el-option-group>
            </el-select>
            <!-- 日期选择器 -->
            <el-date-picker
              v-else-if="column.searchType === 'date'"
              v-model="searchForm[column.prop]"
              type="date"
              :placeholder="`请选择${column.label}`"
              style="width: 200px"
            />
            <!-- 日期范围选择器 -->
            <el-date-picker
              v-else-if="column.searchType === 'daterange'"
              v-model="searchForm[column.prop]"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              style="width: 240px"
            />
          </el-form-item>
        </template>

        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
          <el-button v-if="hasMoreSearchColumns" type="text" @click="toggleSearchExpand">
            {{ searchExpanded ? '收起' : '展开' }}
            <i :class="searchExpanded ? 'el-icon-arrow-up ml-1' : 'el-icon-arrow-down ml-1'"></i>
          </el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 工具栏 -->
    <div v-if="showToolbar" class="table-toolbar">
      <div class="toolbar-left">
        <slot name="toolbar-left" />
      </div>
      <div class="toolbar-right">
        <slot name="toolbar-right" />
        <el-tooltip v-if="showRefresh" content="刷新" placement="top">
          <el-button icon="el-icon-refresh" circle @click="handleRefresh" />
        </el-tooltip>
      </div>
    </div>

    <!-- 表格 -->
    <div class="table-container">
      <el-table
        ref="tableRef"
        v-loading="loading"
        :data="tableData"
        :height="tableHeight||undefined"
        :stripe="stripe"
        :border="border"
        :size="size"
        :row-key="rowKey"
        @selection-change="handleSelectionChange"
        @sort-change="handleSortChange"
        @row-click="handleRowClick"
        @row-dblclick="handleRowDblClick"
      >
        <!-- 展开列 -->
        <el-table-column v-if="showExpand" type="expand" width="55" align="center" fixed="left">
          <template slot-scope="props">
            <slot name="expand" :row="props.row" :index="props.$index" />
          </template>
        </el-table-column>

        <!-- 选择列 -->
        <el-table-column
          v-if="showSelection"
          type="selection"
          width="55"
          align="center"
          fixed="left"
        />

        <!-- 序号列 -->
        <el-table-column
          v-if="showIndex"
          type="index"
          label="序号"
          width="60"
          align="center"
          fixed="left"
          :index="getIndex"
        />

        <!-- 数据列 -->
        <template v-for="column in visibleColumns">
          <el-table-column
            :key="column.prop"
            :prop="column.prop"
            :label="column.label"
            :width="column.width"
            :min-width="column.minWidth"
            :fixed="column.fixed"
            :align="column.align || 'center'"
            :sortable="column.sortable"
            :show-overflow-tooltip="column.showOverflowTooltip !== false"
          >
            <template slot-scope="{ row, column: col, $index }">
              <slot
                :name="column.prop"
                :row="row"
                :column="col"
                :index="$index"
                :value="row[column.prop]"
              >
                <!-- 默认渲染 -->
                <span v-if="!column.render">{{ row[column.prop] }}</span>
                <!-- 自定义渲染 -->
                <component
                  v-else
                  :is="column.render"
                  :row="row"
                  :column="col"
                  :index="$index"
                  :value="row[column.prop]"
                />
              </slot>
            </template>
          </el-table-column>
        </template>

        <!-- 操作列 -->
        <el-table-column
          v-if="$slots.action"
          label="操作"
          :width="actionWidth"
          :fixed="actionFixed"
          align="center"
        >
          <template slot-scope="{ row, $index }">
            <slot name="action" :row="row" :index="$index" />
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页 -->
    <div v-if="showPagination" class="pagination-container">
      <el-pagination
        :current-page="currentPage"
        :page-size="pageSize"
        :total="total"
        :page-sizes="pageSizes"
        :layout="paginationLayout"
        :background="true"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-ui'

export default {
  name: 'ProTable',
  components: {},
  props: {
    columns: {
      type: Array,
      required: true
    },
    data: {
      type: Array,
      default: () => []
    },
    request: {
      type: Function,
      default: null
    },
    rowKey: {
      type: String,
      default: 'id'
    },
    showSelection: {
      type: Boolean,
      default: false
    },
    showIndex: {
      type: Boolean,
      default: false
    },
    showExpand: {
      type: Boolean,
      default: false
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    showToolbar: {
      type: Boolean,
      default: false
    },
    showRefresh: {
      type: Boolean,
      default: false
    },
    showColumnSetting: {
      type: Boolean,
      default: false
    },
    stripe: {
      type: Boolean,
      default: true
    },
    border: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: 'default'
    },
    tableHeight: {
      type: [Number, String],
      default: ''
    },
    actionWidth: {
      type: [Number, String],
      default: 200
    },
    actionFixed: {
      type: [Boolean, String],
      default: 'right'
    },
    pageSizes: {
      type: Array,
      default: () => [10, 20, 50, 100]
    },
    paginationLayout: {
      type: String,
      default: 'total, sizes, prev, pager, next, jumper'
    },
    defaultPageSize: {
      type: Number,
      default: 20
    }
  },
  data() {
    return {
      loading: false,
      tableData: [],
      total: 0,
      currentPage: 1,
      pageSize: this.defaultPageSize,
      searchForm: {},
      searchExpanded:false
    }
  },
  computed: {
      searchColumns() {
        const searchableColumns = this.columns.filter(col => col.searchable && col.prop)
        if (this.searchExpanded) {
          return searchableColumns
        }
        return searchableColumns.slice(0, 3)
      },
      visibleColumns() {
        return this.columns.filter(column => column.inTable !== false && column.prop !== 'action')
      },
      hasMoreSearchColumns() {
        return this.columns.filter(col => col.searchable && col.prop).length > 3
      },
  },
  watch: {
    data: {
      handler(newVal) {
        if (newVal) {
          this.tableData = newVal
          this.total = newVal.length
        }
      },
      immediate: true
    },
    columns: {
      handler() {
        this.initSearchForm()
      },
      immediate: true
    }
  },
  mounted() {
    this.initSearchForm()
    // 初始化为false，确保默认只显示3个搜索列
    this.searchExpanded = false
    this.$nextTick(() => {
      this.loadData()
    })
  },
  methods: {
    initSearchForm() {
      this.columns.forEach(column => {
        if (column.searchable && column.prop) {
          this.$set(this.searchForm, column.prop, '')
        }
      })
    },

    getIndex(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1
    },

    async loadData() {
      if (this.data.length > 0) {
        // 使用传入的数据
        this.tableData = this.data
        this.total = this.data.length
        return
      }

      if (!this.request) {
        console.warn('ProTable: 未提供 data 或 request 属性')
        return
      }

      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          ...this.searchForm
        }

        const response = await this.request(params)

        if (response.success !== false) {
          this.tableData = response.data || []
          this.total = response.total || 0
        } else {
          ElMessage.error(response.message || '数据加载失败')
        }
      } catch (error) {
        console.error('ProTable: 数据加载失败', error)
        ElMessage.error('数据加载失败')
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.currentPage = 1
      this.loadData()
      this.$emit('search', { ...this.searchForm })
    },

    handleReset() {
      this.$refs.searchFormRef.resetFields()
      Object.keys(this.searchForm).forEach(key => {
        this.$set(this.searchForm, key, '')
      })
      this.currentPage = 1
      this.loadData()
    },

    handleRefresh() {
      this.loadData()
      this.$emit('refresh')
    },

    toggleSearchExpand() {
      this.searchExpanded = !this.searchExpanded
    },

    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadData()
    },

    handleCurrentChange(page) {
      this.currentPage = page
      this.loadData()
    },

    handleSelectionChange(selection) {
      this.$emit('selectionChange', selection)
    },

    handleSortChange(sortInfo) {
      this.$emit('sortChange', sortInfo)
      if (this.request) {
        this.loadData()
      }
    },

    handleRowClick(row, column, event) {
      this.$emit('rowClick', row, column, event)
    },

    handleRowDblClick(row, column, event) {
      this.$emit('rowDblClick', row, column, event)
    },

    // 暴露的方法
    refresh() {
      this.handleRefresh()
    },

    search() {
      this.handleSearch()
    },

    reset() {
      this.handleReset()
    },

    getTableRef() {
      return this.$refs.tableRef
    },

    getSearchForm() {
      return this.searchForm
    },

    getTableList() {
      return this.loadData()
    }
  }
}
</script>

<style scoped>
.pro-table {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-form {
  padding: 16px;
  /* background-color: #f5f7fa; */
  border-bottom: 1px solid #e8e8e8;
}

.search-form-content {
  margin-bottom: 0;
}

.search-form-content >>> .el-form-item {
  margin-bottom: 16px;
}

.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e8e8e8;
}

.toolbar-left {
  display: flex;
  align-items: center;
  margin-right: 10px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  margin-left: 10px;
}

.table-container {
  padding: 16px;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  padding: 16px;
  border-top: 1px solid #e8e8e8;
}
</style>
